// MOAT-01 measured pilot — shared shapes + formatting for the merchant page (Pilot.tsx) and the
// Kernvel-staff page (StaffPilots.tsx). Both read the SAME backend report builder
// (app/services/moat01/report.py); nothing here recomputes the metric.

export type PilotStatus = "draft" | "active" | "stopped" | "archived";

export interface PilotDocument {
  id: string;
  pilot_key: string;
  version: number;
  status: PilotStatus;
  config_digest: string;
  config: {
    cohort_shop_ids: string[];
    duration_days: number;
    target_listings_per_arm: number;
    exposure: string;
    control: string;
    assignment: { salt: string; exposed_share_bp: number; max_vendors_per_arm: number };
    budget: { max_ai_units_per_shop_per_day: number; units_per_assist: number };
    metric: { id: string; definition: string; unit: string; direction: string };
    [key: string]: unknown;
  };
  created_by: string;
  created_at: string | null;
  activated_at: string | null;
  ends_at: string | null;
  stopped_at: string | null;
  stop_reason: string | null;
  stopped_by: string | null;
  archived_at: string | null;
}

export interface ArmResult {
  listings_created: number;
  approved: number;
  pending_censored: number;
  excluded: number;
  median_seconds: number | null;
}

export interface PilotMetric {
  metric: string;
  state: "estimated" | "insufficient_data";
  decision: "exposed_faster" | "exposed_slower" | "no_detectable_difference" | "insufficient_data";
  final: boolean;
  arms: { exposed: ArmResult; control: ArmResult };
  difference_seconds: number | null;
  relative_change_pct: number | null;
  ci_low_seconds: number | null;
  ci_high_seconds: number | null;
  confidence_level_pct: number;
  target_listings_per_arm: number;
  sample_complete: boolean;
}

export interface PilotReport {
  pilot: PilotDocument;
  pre_registration: { metric: Record<string, unknown>; analysis: Record<string, unknown>; config_digest: string };
  metric: PilotMetric;
  assistance: {
    event_counts: Record<string, number>;
    vendors_by_arm: Record<string, number>;
    assist_error_rate_bp: number | null;
    assist_calls: number;
    units_by_day: Record<string, number>;
    daily_unit_cap: number;
    max_vendors_per_arm: number;
    stop: { reason: string | null; at: string | null; by: string | null };
  };
  per_shop?: Record<string, PilotMetric>;
}

/** 7260 -> "2h 1m"; negative keeps its sign; null -> "—" (never 0 for an unknown). */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const sign = seconds < 0 ? "-" : "";
  let rest = Math.round(Math.abs(seconds));
  const days = Math.floor(rest / 86_400);
  rest -= days * 86_400;
  const hours = Math.floor(rest / 3_600);
  rest -= hours * 3_600;
  const minutes = Math.floor(rest / 60);
  const parts = [days ? `${days}d` : "", hours ? `${hours}h` : "", `${minutes}m`].filter(Boolean);
  return sign + parts.join(" ");
}

const DECISIONS: Record<PilotMetric["decision"], string> = {
  exposed_faster: "Vendors with AI assistance reached approval faster",
  exposed_slower: "Vendors with AI assistance reached approval slower",
  no_detectable_difference: "No detectable difference between the two groups",
  insufficient_data: "Not enough approved listings yet to estimate",
};

export function decisionText(metric: PilotMetric): string {
  const base = DECISIONS[metric.decision] ?? metric.decision;
  return metric.final || metric.decision === "insufficient_data" ? base : `${base} (interim — pilot still running)`;
}

const STOP_REASONS: Record<string, string> = {
  ai_spend_cap_reached: "AI spend cap reached",
  assist_error_rate_exceeded: "Assistance error rate above 5%",
  grounded_claim_violation: "Unsupported-claim guard fired",
  merchant_disabled: "Disabled by the merchant",
  completed_duration: "Completed (planned duration)",
  completed_sample: "Completed (listing target reached)",
  staff_stopped: "Stopped by Kernvel staff",
};

export function stopReasonText(reason: string | null | undefined): string {
  if (!reason) return "—";
  return STOP_REASONS[reason] ?? reason;
}
